import {
  Home,
  Search,
  User,
  Wrench,
  Briefcase,
  DollarSign,
  Star,
  Clock,
  Bell,
  Mail,
  Info,
  Sparkles,
  BarChart3,
  ShieldAlert,
  FileCheck,
  Landmark,
  Users,
  MessageSquare,
  MoreHorizontal,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import { getEmployeeRoute } from '../utils/employeeRoutes';
import { getRoleDashboardPath } from '../utils/roleHomeRoutes';
import type { UserType } from '../types';

export interface NavLinkConfig {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  activePattern: RegExp;
}

/** 桌面頂欄與漢堡選單（md 以上）完整導覽 */
export function getHeaderNavItems(userType?: UserType | null): NavLinkConfig[] {
  switch (userType) {
    case 'admin':
      return [
        nav('admin-dashboard', '管理儀表板', '/admin/dashboard', Home, /^\/admin\/dashboard/),
        nav('admin-technicians', '技師審核', '/admin/technicians', Wrench, /^\/admin\/technicians/),
        nav('admin-identity', '身分驗證審核', '/admin/identity-verifications', FileCheck, /^\/admin\/identity-verifications/),
        nav('admin-company-biz', '公司商登核對', '/admin/company-business-verification', Landmark, /^\/admin\/company-business-verification/),
        nav('admin-disputes', '爭議與仲裁', '/admin/disputes', ShieldAlert, /^\/admin\/disputes/),
        nav('admin-commission', '抽成設定', '/admin/commission', DollarSign, /^\/admin\/commission/),
        nav('admin-stats', '佣金統計', '/commission-stats', BarChart3, /^\/commission-stats/),
        nav('admin-blacklist', '黑名單管理', '/admin/blacklist', ShieldAlert, /^\/admin\/blacklist/),
      ];
    case 'company':
      return [
        nav('company-dashboard', '儀表板', '/company/dashboard', Home, /^\/company\/dashboard/),
        nav('company-jobs', '案件管理', '/company/jobs', Briefcase, /^\/company\/jobs/),
        nav('company-profile', '公司資料', '/company/profile', User, /^\/company\/profile/),
        nav('company-employees', '員工管理', '/company/employees', Users, /^\/company\/employees/),
        nav('company-reviews', '評價管理', '/company/reviews', Star, /^\/company\/reviews/),
      ];
    case 'craftsman':
      return [
        nav('craftsman-dashboard', '儀表板', '/craftsman/dashboard', Home, /^\/craftsman\/dashboard/),
        nav('craftsman-jobs', '接案管理', '/craftsman/jobs', Briefcase, /^\/craftsman\/jobs/),
        nav('craftsman-profile', '工匠資料', '/craftsman/profile', User, /^\/craftsman\/profile/),
        nav('craftsman-earnings', '收入統計', '/craftsman/earnings', DollarSign, /^\/craftsman\/earnings/),
        nav('craftsman-notifications', '通知中心', '/craftsman/notifications', Bell, /^\/craftsman\/notifications/),
      ];
    case 'customer':
      return [
        nav('customer-home', '首頁', '/', Home, /^\/$/),
        nav('customer-search', '搜尋工匠', '/search', Search, /^\/(search|craftsman-search)/),
        nav('customer-history', '服務歷史', '/service-history', Clock, /^\/service-history/),
        nav('customer-notifications', '通知中心', '/customer/notifications', Bell, /^\/customer\/notifications/),
        nav('customer-profile', '個人中心', '/profile', User, /^\/profile/),
      ];
    case 'employee':
    case 'customer_service':
    case 'administrative_staff':
    case 'other_staff':
      return [
        nav('employee-dashboard', '工作台', getEmployeeRoute('dashboard', userType), Home, /^\/employee\/dashboard/),
        nav('employee-jobs', '我的案件', getEmployeeRoute('jobs', userType), Briefcase, /^\/employee\/jobs/),
        nav('employee-profile', '個人中心', getEmployeeRoute('profile', userType), User, /^\/employee\/profile/),
        nav('employee-risk', '風控儀表板', '/risk-monitoring', BarChart3, /^\/risk-monitoring/),
      ];
    case 'finance_staff':
      return [
        nav('finance-dashboard', '工作台', getEmployeeRoute('dashboard', 'finance_staff'), Home, /^\/employee\/dashboard/),
        nav('finance-jobs', '我的案件', getEmployeeRoute('jobs', 'finance_staff'), Briefcase, /^\/employee\/jobs/),
        nav('finance-profile', '個人中心', getEmployeeRoute('profile', 'finance_staff'), User, /^\/employee\/profile/),
        nav('finance-earnings', '公司收入統計', '/company/earnings', DollarSign, /^\/company\/earnings/),
        nav('finance-risk', '風控儀表板', '/risk-monitoring', BarChart3, /^\/risk-monitoring/),
      ];
    default:
      return [
        nav('public-home', '首頁', '/', Home, /^\/$/),
        nav('public-about', '服務介紹', '/about', Sparkles, /^\/about/),
        nav('public-search', '搜尋工匠', '/search', Search, /^\/(search|craftsman-search)/),
        nav('public-about2', '關於我們', '/about', Info, /^\/about/),
        nav('public-contact', '聯絡我們', '/contact', Mail, /^\/contact/),
      ];
  }
}

/** 手機底欄：3–4 個高頻入口 */
export function getBottomNavItems(userType?: UserType | null): NavLinkConfig[] | null {
  if (!userType) {
    return [
      nav('home', '首頁', '/', Home, /^\/$/),
      nav('search', '搜尋', '/search', Search, /^\/(search|craftsman-search)/),
      nav('request', '發案', '/service-request', Wrench, /^\/(service-request|request)/),
      nav('more', '更多', '/more', MoreHorizontal, /^\/more/),
    ];
  }

  switch (userType) {
    case 'customer':
      return [
        nav('home', '首頁', '/', Home, /^\/$/),
        nav('search', '搜尋', '/search', Search, /^\/(search|craftsman-search)/),
        nav('request', '發案', '/service-request', Wrench, /^\/(service-request|request)/),
        nav('profile', '我的', '/profile', User, /^\/profile/),
      ];
    case 'craftsman':
      return [
        nav('dashboard', '工作台', getRoleDashboardPath('craftsman'), Home, /^\/craftsman\/dashboard/),
        nav('jobs', '接案', '/craftsman/jobs', Briefcase, /^\/craftsman\/jobs/),
        nav('earnings', '收入', '/craftsman/earnings', DollarSign, /^\/craftsman\/earnings/),
        nav('profile', '我的', '/craftsman/profile', User, /^\/craftsman\/profile/),
      ];
    case 'company':
      return [
        nav('dashboard', '工作台', getRoleDashboardPath('company'), Home, /^\/company\/dashboard/),
        nav('jobs', '案件', '/company/jobs', Briefcase, /^\/company\/jobs/),
        nav('reviews', '評價', '/company/reviews', Star, /^\/company\/reviews/),
        nav('profile', '我的', '/company/profile', User, /^\/company\/profile/),
      ];
    case 'employee':
    case 'customer_service':
    case 'administrative_staff':
    case 'finance_staff':
    case 'other_staff':
      return [
        nav('dashboard', '工作台', getEmployeeRoute('dashboard', userType), Home, /^\/employee\/dashboard/),
        nav('jobs', '案件', getEmployeeRoute('jobs', userType), Briefcase, /^\/employee\/jobs/),
        nav('profile', '我的', getEmployeeRoute('profile', userType), User, /^\/employee\/profile/),
      ];
    case 'admin':
      return [
        nav('dashboard', '工作台', '/admin/dashboard', Home, /^\/admin\/dashboard/),
        nav('technicians', '技師審核', '/admin/technicians', Wrench, /^\/admin\/technicians/),
        nav('disputes', '爭議', '/admin/disputes', ShieldAlert, /^\/admin\/disputes/),
        nav('more', '更多', '/more', MoreHorizontal, /^\/more/),
      ];
    default:
      return null;
  }
}

/** 「我的」頁頂部快捷入口（手機） */
export function getProfileSecondaryLinks(userType?: UserType | null): NavLinkConfig[] {
  switch (userType) {
    case 'customer':
      return [
        nav('history', '服務歷史', '/service-history', Clock, /^\/service-history/),
        nav('notifications', '通知中心', '/customer/notifications', Bell, /^\/customer\/notifications/),
        nav('messages', '消息', '/messages', MessageSquare, /^\/messages/),
      ];
    case 'craftsman':
      return [
        nav('notifications', '通知中心', '/craftsman/notifications', Bell, /^\/craftsman\/notifications/),
        nav('messages', '消息', '/messages', MessageSquare, /^\/messages/),
      ];
    case 'company':
      return [
        nav('employees', '員工管理', '/company/employees', Users, /^\/company\/employees/),
        nav('earnings', '收入報表', '/company/earnings', DollarSign, /^\/company\/earnings/),
        nav('messages', '消息', '/messages', MessageSquare, /^\/messages/),
      ];
    case 'employee':
    case 'customer_service':
    case 'administrative_staff':
    case 'other_staff':
      return [
        nav('risk', '風控儀表板', '/risk-monitoring', BarChart3, /^\/risk-monitoring/),
        nav('messages', '消息', '/messages', MessageSquare, /^\/messages/),
      ];
    case 'finance_staff':
      return [
        nav('earnings', '公司收入統計', '/company/earnings', DollarSign, /^\/company\/earnings/),
        nav('risk', '風控儀表板', '/risk-monitoring', BarChart3, /^\/risk-monitoring/),
        nav('messages', '消息', '/messages', MessageSquare, /^\/messages/),
      ];
    default:
      return [];
  }
}

/** 「更多」頁：底欄放不下的次要入口 */
export function getMorePageLinks(userType?: UserType | null): NavLinkConfig[] {
  if (!userType) {
    return [
      nav('about', '服務介紹', '/about', Sparkles, /^\/about/),
      nav('contact', '聯絡我們', '/contact', Mail, /^\/contact/),
      nav('legal', '法律文件', '/legal', FileText, /^\/legal/),
    ];
  }

  if (userType === 'admin') {
    return [
      nav('identity', '身分驗證審核', '/admin/identity-verifications', FileCheck, /^\/admin\/identity-verifications/),
      nav('company-biz', '公司商登核對', '/admin/company-business-verification', Landmark, /^\/admin\/company-business-verification/),
      nav('commission', '抽成設定', '/admin/commission', DollarSign, /^\/admin\/commission/),
      nav('stats', '佣金統計', '/commission-stats', BarChart3, /^\/commission-stats/),
      nav('blacklist', '黑名單管理', '/admin/blacklist', ShieldAlert, /^\/admin\/blacklist/),
    ];
  }

  if (
    userType === 'employee' ||
    userType === 'customer_service' ||
    userType === 'administrative_staff' ||
    userType === 'other_staff'
  ) {
    const links = getProfileSecondaryLinks(userType);
    if (userType === 'administrative_staff') {
      return [
        ...links,
        nav('documents', '文件管理', getEmployeeRoute('documents', userType), FileText, /^\/employee\/documents/),
      ];
    }
    return links;
  }

  if (userType === 'finance_staff') {
    return getProfileSecondaryLinks('finance_staff');
  }

  return [];
}

/** 客戶／工匠：通知已在頂欄鈴鐺，桌面導覽隱藏重複項 */
export function getHeaderNavItemsForDisplay(userType?: UserType | null): NavLinkConfig[] {
  const items = getHeaderNavItems(userType);
  const hideByRole: Partial<Record<UserType, string>> = {
    customer: '/customer/notifications',
    craftsman: '/craftsman/notifications',
  };
  const hidePath = userType ? hideByRole[userType] : undefined;
  if (!hidePath) return items;
  return items.filter((item) => item.path !== hidePath);
}

function nav(
  id: string,
  label: string,
  path: string,
  icon: LucideIcon,
  activePattern: RegExp
): NavLinkConfig {
  return { id, label, path, icon, activePattern };
}
